// Percorso file: src/components/SeasonStatusBar.jsx
//
// BARRA DI STATO DELLA STAGIONE (doc 07 §8.2) — componente condiviso.
//
// Perché sta qui e non più dentro SeasonManagement.jsx: il dato è lo
// stesso in tutta l'app — vive nella sede condivisa (AuthContext, YS-5)
// — ma la sua VESTE viveva in un foglio di pagina e il suo JSX in tre
// blocchi fratelli non esportati. La prima finestra che avesse avuto
// bisogno di dichiarare lo stato della stagione avrebbe dovuto
// ricopiarli tutti e due.
//
// Nasce col passo A1 come estrazione FEDELE (a schermo non cambiava
// nulla) e col passo A2 va a norma del doc 07: la messa a norma è quasi
// tutta nel foglio, ma tocca anche qui in due punti — la fascia si rende
// SEMPRE (in caricamento diceva niente: ora dice «Season not loaded», e
// il layout non salta più) e la pastiglia riusa la forma condivisa.
//
// Il CSS lo importa questo file, non le pagine: chi usa la barra non
// deve sapere in quale foglio vive. Sono due:
//   AdminTools.css      — la FORMA della pastiglia, `.qpsc-pill` (§2)
//   SeasonStatusBar.css — tutto il resto, e il colore della pastiglia
// (AdminTools.css ha ogni selettore ancorato a una classe con prefisso
// `qpsc-` — nessun selettore di elemento nudo, nessuna classe senza
// prefisso: nessuna regola globale, e nessuna pagina viene toccata dal
// solo fatto di importarlo.)

import React from "react";
import { FaExchangeAlt } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import "./AdminTools.css";
import "./SeasonStatusBar.css";

// La sola coerenza ammessa dal server per i due anni (server.js, validazione PUT /season-config):
//   enrollment === operating           -> stagione unica
//   enrollment === operating + 1        -> sovrapposizione (transizione)
// Qualunque altra coppia il server la rifiuta (400): qui la si segnala, non la si nasconde.
//
// ⚠️ È ESPORTATA, e non per comodità. La fase non serve solo alla fascia: Season
// Management ci decide QUALI PULSANTI esistono («apri» solo in stagione unica,
// «chiudi/ritira» solo in sovrapposizione). Se la pagina ne tenesse una copia
// propria, due copie della stessa regola dovrebbero restare d'accordo per sempre,
// e il giorno in cui divergessero la barra direbbe una fase e i comandi ne
// eseguirebbero un'altra. Sede unica, come per il gate dei ruoli.
export function calcolaFase(enrollment, operating) {
  if (enrollment == null || operating == null) return "unknown";
  if (enrollment === operating) return "single";
  if (enrollment === operating + 1) return "overlap";
  return "inconsistent";
}

function FasciaFase({ fase, enrollment, operating }) {
  const config = {
    single: {
      classe: "qpsc-seasonbar__phase--single",
      testo: `Single season — ${operating}`,
    },
    overlap: {
      classe: "qpsc-seasonbar__phase--overlap",
      testo: `Transition — ${operating} and ${enrollment} overlap`,
    },
    inconsistent: {
      classe: "qpsc-seasonbar__phase--inconsistent",
      testo: "Inconsistent seasons — check configuration",
    },
    unknown: {
      classe: "qpsc-seasonbar__phase--unknown",
      testo: "Season not loaded",
    },
  }[fase];

  return (
    <div className={`qpsc-seasonbar__phase ${config.classe}`}>
      <FaExchangeAlt className="qpsc-seasonbar__phase-icon" aria-hidden="true" />
      <span>{config.testo}</span>
    </div>
  );
}

/**
 * Barra di stato della stagione del club (doc 07 §8.2): i due anni, la fascia
 * di fase, la riga delle iscrizioni.
 *
 * Dichiara come sta il CLUB, non che annata una pagina stia guardando: un
 * selettore «Season viewed» (§8.1) è un'altra cosa e non si confonde con questa.
 *
 * I valori vengono dalla sede condivisa (AuthContext, YS-5): il componente non
 * fa letture proprie e non conosce ruoli — il cancello è di chi lo monta.
 *
 * Prop:
 *   showPhase       — false toglie la fascia di fase
 *   showEnrollments — false toglie la riga delle iscrizioni
 *   note            — testo (o nodo) accanto alla pastiglia. NON è del
 *                     componente: parla del gesto che comanda la pagina che lo
 *                     monta. Assente, la riga della nota non si rende.
 *   override        — { enrollmentSeason, operatingSeason, enrollmentsOpen }.
 *                     Facoltativa, e solo per la pagina che SCRIVE la stagione:
 *                     se c'è, i tre valori si prendono da lei invece che dal
 *                     contesto.
 *                     ⚠️ `seasonStatus` viene SEMPRE dal contesto: dire
 *                     «caricata» o «non caricata» spetta alla sede condivisa, e
 *                     una pagina che credesse di saperlo meglio spegnerebbe
 *                     l'unico segnale di guasto che la barra ha.
 *                     ⚠️ Serve a Season Management, che subito dopo un comando
 *                     riuscito conosce la cassettina appena scritta prima che la
 *                     sede condivisa la rilegga — e se la rilettura fallisce
 *                     resta l'unica a conoscerla (AuthContext: `refreshSeason`
 *                     torna null su qualunque guasto, e la copia locale non si
 *                     azzera più). Senza questa prop la barra direbbe gli anni di
 *                     PRIMA mentre i comandi sotto dicono i NUOVI, dopo
 *                     un'azione RIUSCITA.
 */
export default function SeasonStatusBar({
  showPhase = true,
  showEnrollments = true,
  note = null,
  override = null,
}) {
  const {
    operatingSeason: operatingDalContesto,
    enrollmentSeason: enrollmentDalContesto,
    seasonStatus,
    enrollmentsOpen: enrollmentsOpenDalContesto,
  } = useAuth();

  // I VALORI EFFETTIVI. `override` vince finché c'è, il contesto governa sempre altrimenti.
  const enrollmentSeason = override ? override.enrollmentSeason : enrollmentDalContesto;
  const operatingSeason = override ? override.operatingSeason : operatingDalContesto;
  const enrollmentsOpen = override ? override.enrollmentsOpen : enrollmentsOpenDalContesto;

  const caricamento = seasonStatus === "loading";

  const fase = calcolaFase(enrollmentSeason, operatingSeason);

  // Si legge solo quando la stagione è davvero arrivata: fuori da "ready" non
  // sappiamo nulla, e la pastiglia resta neutra invece di dichiarare uno stato
  // che non abbiamo letto.
  // Chiuse SOLO su un `false` esplicito — stesso metro tollerante del server.
  const statoIscrizioni =
    seasonStatus !== "ready"
      ? "unknown"
      : enrollmentsOpen === false
      ? "closed"
      : "open"; // "open" | "closed" | "unknown"

  return (
    <div className="qpsc-seasonbar">
      {/* I due valori */}
      <div className="qpsc-seasonbar__values">
        <div className="qpsc-seasonbar__value">
          <span className="qpsc-seasonbar__label">Enrollment season</span>
          <span className="qpsc-seasonbar__number">
            {caricamento ? "…" : enrollmentSeason ?? "—"}
          </span>
        </div>
        <div className="qpsc-seasonbar__value">
          <span className="qpsc-seasonbar__label">Operating season</span>
          <span className="qpsc-seasonbar__number">
            {caricamento ? "…" : operatingSeason ?? "—"}
          </span>
        </div>
      </div>

      {/* Fase — SEMPRE resa, anche in caricamento (doc 07 §8.2).
          ⚠️ Prima viveva dentro `{!caricamento && …}` e in caricamento non
          esisteva nel DOM: la fascia compariva a lettura avvenuta e il layout
          saltava. Fuori da "ready" i due anni valgono `null` (AuthContext),
          quindi `calcolaFase` dà «unknown» e la fascia dice «Season not
          loaded» — una riga che il lettore capisce, al posto di un vuoto. */}
      {showPhase && (
        <FasciaFase fase={fase} enrollment={enrollmentSeason} operating={operatingSeason} />
      )}

      {/* Stato iscrizioni (terzo valore) */}
      {showEnrollments && (
        <div className="qpsc-seasonbar__enrollments">
          {/* ⚠️ LA FORMA È `.qpsc-pill`, la classe condivisa del §2 che vive in
              AdminTools.css: qui si aggiunge solo il COLORE, che il §2 non
              prevede perché lo stato delle iscrizioni non è una categoria.
              Stesso riuso della testata di View Profile (§11.5). Nessuna forma
              nuova, nessuna copia della pastiglia.
              ⚠️ «Enrollments not loaded» ha perso il trattino lungo: la forma
              condivisa rende il testo in MAIUSCOLO (§3.2, ammesso solo qui), e
              «ENROLLMENTS — NOT LOADED» si legge male. È l'unico testo che
              questa fetta cambia. */}
          <span
            className={
              "qpsc-pill " +
              (statoIscrizioni === "open"
                ? "qpsc-seasonbar__pill--open"
                : statoIscrizioni === "closed"
                ? "qpsc-seasonbar__pill--closed"
                : "qpsc-seasonbar__pill--unknown")
            }
          >
            {statoIscrizioni === "open"
              ? "Enrollments open"
              : statoIscrizioni === "closed"
              ? "Enrollments closed"
              : "Enrollments not loaded"}
          </span>
          {note && <span className="qpsc-seasonbar__note">{note}</span>}
        </div>
      )}
    </div>
  );
}
